import { useCallback, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Body,
  Button,
  Caption,
  Card,
  Eyebrow,
  Field,
  Heading,
  I,
  Input,
  Row,
  Screen,
  ScreenBody,
  Select,
  Stack,
  Textarea,
} from '@leanin/ui';
import { CountryPicker } from '@/components/CountryPicker';
import { BodyWithAside } from '@/components/page-shell';
import { useSubmitNetworkLeaderApplication } from '@/lib/mutations/groups';

export const Route = createFileRoute('/_authed/networks/new')({
  component: NetworkApplyPage,
});

const REGION_OPTIONS = [
  { value: 'EMEA',           label: 'EMEA' },
  { value: 'Americas',       label: 'Americas' },
  { value: 'APAC',           label: 'APAC' },
  { value: 'Global Affinity', label: 'Global Affinity' },
];

const COMMUNITY_SIZE_OPTIONS = [
  { value: '50-100',  label: '50–100 members' },
  { value: '100-250', label: '100–250 members' },
  { value: '250+',    label: '250+ members' },
];

type Errors = Partial<Record<
  'firstName' | 'lastName' | 'email' | 'country' | 'region' |
  'proposedName' | 'city' | 'whyCreate' | 'eventExperience' | 'leadershipPlan',
  string
>>;

// Single-line text entry that expands on Cmd/Ctrl+Enter, auto-resizing to fit content.
function ExpandableInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const autoResize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      const el = ref.current;
      if (!el) return;
      const start = el.selectionStart ?? value.length;
      const end = el.selectionEnd ?? value.length;
      const next = value.slice(0, start) + '\n' + value.slice(end);
      onChange(next);
      requestAnimationFrame(() => {
        if (!ref.current) return;
        ref.current.selectionStart = ref.current.selectionEnd = start + 1;
        autoResize();
      });
    }
  };

  return (
    <Textarea
      ref={ref}
      rows={1}
      value={value}
      placeholder={placeholder}
      onKeyDown={handleKeyDown}
      onChange={(e) => { onChange(e.target.value); autoResize(); }}
      style={{ resize: 'none', overflow: 'hidden' }}
    />
  );
}

function NetworkApplyPage() {
  // YOUR DETAILS
  const [firstName, setFirstName]   = useState('');
  const [lastName, setLastName]     = useState('');
  const [email, setEmail]           = useState('');
  const [jobTitle, setJobTitle]     = useState('');
  const [countryCode, setCountryCode] = useState<string | undefined>(undefined);
  const [region, setRegion]         = useState('');
  const [phone, setPhone]           = useState('');

  // YOUR COMMUNITY
  const [circles, setCircles]               = useState('');
  const [otherCommunity, setOtherCommunity] = useState('');
  const [communitySize, setCommunitySize]   = useState('');

  // YOUR NETWORK
  const [proposedName, setProposedName] = useState('');
  const [city, setCity]                 = useState('');

  // YOUR MOTIVATION
  const [whyCreate, setWhyCreate]           = useState('');
  const [eventExperience, setEventExperience] = useState('');
  const [leadershipPlan, setLeadershipPlan] = useState('');

  const [errors, setErrors]     = useState<Errors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const submitApplication = useSubmitNetworkLeaderApplication();

  const onSubmit = () => {
    const next: Errors = {};
    if (!firstName.trim())       next.firstName       = 'Required.';
    if (!lastName.trim())        next.lastName        = 'Required.';
    if (!email.trim())           next.email           = 'Required.';
    if (!countryCode)            next.country         = 'Required.';
    if (!region)                 next.region          = 'Required.';
    if (!proposedName.trim())    next.proposedName    = 'Required.';
    if (!city.trim())            next.city            = 'Required.';
    if (!whyCreate.trim())       next.whyCreate       = 'Required.';
    if (!eventExperience.trim()) next.eventExperience = 'Required.';
    if (!leadershipPlan.trim())  next.leadershipPlan  = 'Required.';
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    setSubmitError(null);
    submitApplication.mutate(
      {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        ...(jobTitle.trim() ? { jobTitle: jobTitle.trim() } : {}),
        ...(countryCode ? { countryCode } : {}),
        region: region as 'EMEA' | 'Americas' | 'APAC' | 'Global Affinity',
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        ...(circles.trim() ? { circles: circles.trim() } : {}),
        ...(otherCommunity.trim() ? { otherCommunity: otherCommunity.trim() } : {}),
        ...(communitySize ? { communitySize } : {}),
        proposedName: proposedName.trim(),
        city: city.trim(),
        whyCreate: whyCreate.trim(),
        eventExperience: eventExperience.trim(),
        leadershipPlan: leadershipPlan.trim(),
      },
      {
        onSuccess: () => setSubmitted(true),
        onError: () => setSubmitError('Something went wrong submitting your application. Please try again.'),
      },
    );
  };

  if (submitted) {
    return (
      <Screen>
        <ScreenBody gap="xl">
          <Stack gap="sm">
            <Eyebrow tone="accent">Application received</Eyebrow>
            <Heading level={1}>Thank you, {firstName}.</Heading>
            <Body size="lg" color="mute">
              We've received your application for the Network Leaders program and will be in touch within 2 weeks.
            </Body>
          </Stack>
          <Card pad="spacious" style={{ maxWidth: 560 }}>
            <Stack gap="lg">
              <Eyebrow tone="accent">What happens next</Eyebrow>
              <Stack gap="md">
                {NEXT_STEPS.map((step, i) => (
                  <Row key={i} gap="md" align="flex-start">
                    <span
                      style={{
                        flexShrink: 0,
                        width: 28,
                        height: 28,
                        borderRadius: 'var(--radius-full)',
                        background: 'color-mix(in srgb, var(--primary) 10%, transparent)',
                        color: 'var(--primary)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        font: 'var(--type-label-sm)',
                      }}
                    >
                      {i + 1}
                    </span>
                    <Body size="md">{step}</Body>
                  </Row>
                ))}
              </Stack>
            </Stack>
          </Card>
        </ScreenBody>
      </Screen>
    );
  }

  return (
    <Screen>
      <ScreenBody gap="xl">

        {/* ── Programme intro ────────────────────────────────────────────── */}
        <Stack gap="sm">
          <Eyebrow>Network Leaders Program</Eyebrow>
          <Heading level={1}>Apply to become a Network leader</Heading>
          <Body size="lg">
            Help accelerate women's progression into leadership roles in your community.
          </Body>
        </Stack>

        <div
          style={{
            display: 'grid',
            // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- CSS grid; no Box prop for grid containers
            gap: 'var(--space-lg)',
            gridTemplateColumns: '1fr',
          }}
        >
          <Card pad="spacious">
            <Stack gap="lg">
              <Stack gap="md">
                <Stack gap="xs">
                  <Heading level={5}>Mission</Heading>
                  <Body size="md" color="mute">
                    The Network Leaders program equips leaders to build Networks that accelerate women's progression into leadership roles.
                  </Body>
                </Stack>
                <Stack gap="xs">
                  <Heading level={5}>Vision</Heading>
                  <Body size="md" color="mute">
                    Lean In Networks are the most trusted community-powered engine accelerating women into leadership worldwide.
                  </Body>
                </Stack>
              </Stack>
              <div style={{ borderTop: 'var(--border-hairline) solid var(--border)' }} />
              <Stack gap="sm">
                <Heading level={5}>What is a Network?</Heading>
                <Body size="md" color="mute">
                  Networks are volunteer-led communities — local or affinity-based — that bring our mission to life globally.
                  They empower women to lead by supporting Circles, hosting leadership events, and connecting members to
                  resources and opportunities at a local level. Networks meet shared standards for quality, governance,
                  and impact, and receive funding, resources, and leadership development through the Network Leaders program.
                </Body>
              </Stack>
            </Stack>
          </Card>
        </div>

        {/* ── Two-column form + sidebar ───────────────────────────────────── */}
        <BodyWithAside
          aside={
            <Stack gap="lg">

              {/* Requirements */}
              <Card pad="default">
                <Stack gap="md">
                  <Stack gap="xs">
                    <Eyebrow>Before you apply</Eyebrow>
                    <Eyebrow tone="accent">Requirements</Eyebrow>
                  </Stack>
                  <Body size="sm" color="mute">
                    To apply to be a Network you need to demonstrate that:
                  </Body>
                  <Stack gap="sm">
                    {REQUIREMENTS.map((req, i) => (
                      <Row key={i} gap="sm" align="flex-start">
                        <span
                          style={{
                            flexShrink: 0,
                            width: 14,
                            height: 14,
                            marginTop: 0,
                            color: 'var(--primary)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {I.check}
                        </span>
                        <Body size="sm">{req}</Body>
                      </Row>
                    ))}
                  </Stack>
                </Stack>
              </Card>

              {/* What happens next */}
              <Card pad="default">
                <Stack gap="md">
                  <Eyebrow tone="accent">What happens next</Eyebrow>
                  <Stack gap="md">
                    {NEXT_STEPS.map((step, i) => (
                      <Row key={i} gap="md" align="flex-start">
                        <span
                          style={{
                            flexShrink: 0,
                            width: 24,
                            height: 24,
                            borderRadius: 'var(--radius-full)',
                            background: 'color-mix(in srgb, var(--primary) 10%, transparent)',
                            color: 'var(--primary)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            font: 'var(--type-label-sm)',
                          }}
                        >
                          {i + 1}
                        </span>
                        <Body size="sm" color="mute">{step}</Body>
                      </Row>
                    ))}
                  </Stack>
                </Stack>
              </Card>

            </Stack>
          }
        >
          {/* ── MAIN COLUMN ─────────────────────────────────────────────── */}
          <Stack gap="lg">

            {/* YOUR DETAILS */}
            <Card pad="spacious">
              <Stack gap="lg">
                <Stack gap="xs">
                  <Eyebrow>Your details</Eyebrow>
                  <Eyebrow tone="accent">About you</Eyebrow>
                </Stack>
                <Row gap="md" align="flex-start">
                  <Field label="First name" required error={errors.firstName} style={{ flex: 1 }}>
                    <Input
                      placeholder="First name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      autoComplete="given-name"
                    />
                  </Field>
                  <Field label="Last name" required error={errors.lastName} style={{ flex: 1 }}>
                    <Input
                      placeholder="Last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      autoComplete="family-name"
                    />
                  </Field>
                </Row>
                <Field label="Email address" required error={errors.email}>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                  />
                </Field>
                <Field label="Job title">
                  <Input
                    placeholder="e.g. Senior Product Manager"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    autoComplete="organization-title"
                  />
                </Field>
                <Row gap="md" align="flex-start">
                  <Field label="Country" required error={errors.country} style={{ flex: 1 }}>
                    <CountryPicker value={countryCode} onChange={setCountryCode} />
                  </Field>
                  <Field
                    label="Region"
                    required
                    error={errors.region}
                    style={{ flex: 1 }}
                  >
                    <Select
                      placeholder="Select a region"
                      value={region}
                      options={REGION_OPTIONS}
                      onChange={(e) => setRegion(e.target.value)}
                    />
                  </Field>
                </Row>
                <Field label="Phone number">
                  <Input
                    type="tel"
                    placeholder="+1 555 000 0000"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    autoComplete="tel"
                  />
                </Field>
              </Stack>
            </Card>

            {/* YOUR COMMUNITY */}
            <Card pad="spacious">
              <Stack gap="lg">
                <Stack gap="xs">
                  <Eyebrow>Your community</Eyebrow>
                  <Eyebrow tone="accent">Who you already lead</Eyebrow>
                </Stack>
                <Field
                  label="Circle(s) you lead"
                  description="If you lead multiple Circles, list them all. Include a link to your Circle profile if you have one. Press ⌘ Return to add another line."
                >
                  <ExpandableInput
                    placeholder="e.g. Lean In London Tech — https://leanin.org/circles/lean-in-london-tech"
                    value={circles}
                    onChange={setCircles}
                  />
                </Field>
                <Field
                  label="Other communities or groups you lead"
                  description="If you lead an existing community outside of Lean In, share the name and a link if available. Press ⌘ Return to add another line."
                >
                  <ExpandableInput
                    placeholder="e.g. Women in Tech Nairobi — https://witnairobi.org"
                    value={otherCommunity}
                    onChange={setOtherCommunity}
                  />
                </Field>
                <Field
                  label="Approximate total community size"
                  description="Across all your Circles and other communities."
                >
                  <Select
                    placeholder="Select a range"
                    value={communitySize}
                    options={[{ value: '', label: 'Prefer not to say' }, ...COMMUNITY_SIZE_OPTIONS]}
                    onChange={(e) => setCommunitySize(e.target.value)}
                  />
                </Field>
              </Stack>
            </Card>

            {/* YOUR NETWORK */}
            <Card pad="spacious">
              <Stack gap="lg">
                <Stack gap="xs">
                  <Eyebrow>Your Network</Eyebrow>
                  <Eyebrow tone="accent">What you're proposing</Eyebrow>
                </Stack>
                <Field
                  label="Proposed Network name"
                  required
                  description={<>
                    All Lean In Networks must use &ldquo;Lean In Network&rdquo; followed by city or metropolitan area, optionally with an affinity descriptor.<br />
                    <strong>Naming convention:</strong> Lean In Network, Singapore · Lean In Network, Silicon Valley · Lean In Network, Cape Town Leaders in Sustainability<br />
                    Networks cannot be named after a country, region, or state.
                  </>}
                  error={errors.proposedName}
                >
                  <Input
                    placeholder="e.g. Lean In Network, Singapore"
                    value={proposedName}
                    onChange={(e) => setProposedName(e.target.value)}
                  />
                </Field>
                <Field
                  label="City or location you primarily operate in"
                  required
                  description="The city or metropolitan area where most of your activity takes place."
                  error={errors.city}
                >
                  <Input
                    placeholder="e.g. Nairobi, Kenya"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                  />
                </Field>
              </Stack>
            </Card>

            {/* YOUR MOTIVATION */}
            <Card pad="spacious">
              <Stack gap="lg">
                <Stack gap="xs">
                  <Eyebrow>Your motivation</Eyebrow>
                  <Eyebrow tone="accent">Tell us more</Eyebrow>
                </Stack>
                <Field
                  label="Why do you want to create a Lean In Network?"
                  required
                  description="Tell us what drives you and what you hope to achieve."
                  error={errors.whyCreate}
                >
                  <Textarea
                    rows={5}
                    value={whyCreate}
                    onChange={(e) => setWhyCreate(e.target.value)}
                  />
                </Field>
                <Field
                  label="Describe your experience organising events"
                  required
                  description="What kinds of events have you run, and how large were they?"
                  error={errors.eventExperience}
                >
                  <Textarea
                    rows={4}
                    value={eventExperience}
                    onChange={(e) => setEventExperience(e.target.value)}
                  />
                </Field>
                <Field
                  label="How do you plan to build your leadership team?"
                  required
                  description="Who might join your team, and what roles do you envision?"
                  error={errors.leadershipPlan}
                >
                  <Textarea
                    rows={4}
                    value={leadershipPlan}
                    onChange={(e) => setLeadershipPlan(e.target.value)}
                  />
                </Field>
              </Stack>
            </Card>

            {submitError !== null && (
              <Body size="sm" color="destructive">{submitError}</Body>
            )}
            <Row justify="space-between" align="center">
              <Caption>* Required fields</Caption>
              <Button
                variant="primary"
                iconRight={I.arrow}
                onClick={onSubmit}
                loading={submitApplication.isPending}
              >
                Submit application
              </Button>
            </Row>
          </Stack>

        </BodyWithAside>
      </ScreenBody>
    </Screen>
  );
}

const REQUIREMENTS = [
  'You lead a Circle or Circles and/or an existing community that has more than 50 people in total.',
  'You have the skills and experience to organise events.',
  'You have the ability to build a leadership team to help run the Network.',
] as const;

const NEXT_STEPS = [
  'Submit your application.',
  'Our team will review your application and be in touch within 2 weeks.',
  'If shortlisted, you will be invited to an informal interview with the Director of the Program to explore alignment, your vision, and learn more about the Network Leaders program.',
] as const;
